import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();
  const done = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      if (cancelled || done.current) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAttempts(++tries);
        if (data.payment_status === "paid") {
          done.current = true;
          await refresh();
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          done.current = true; setStatus("failed"); return;
        }
        if (tries >= 20) { done.current = true; setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setAttempts(++tries);
        if (e?.response?.status === 404) { done.current = true; setStatus("error"); return; }
        if (tries >= 20) { done.current = true; setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2] px-4">
      <div className="w-full max-w-md text-center bg-white rounded-2xl border border-stone-200 p-10">
        {status === "polling" && <>
          <Loader2 className="mx-auto animate-spin text-[#C85A32]" size={40}/>
          <div className="font-serif-display text-2xl font-bold mt-4">Confirming your payment…</div>
          <div className="text-sm text-stone-500 mt-1 font-mono-code">Check {attempts}/20</div>
        </>}
        {status === "paid" && <>
          <CheckCircle2 className="mx-auto text-emerald-600" size={48}/>
          <div className="font-serif-display text-3xl font-bold mt-4">Welcome to Pro</div>
          <p className="text-stone-600 mt-2">Unlimited trips and AI generations are unlocked. Bon voyage.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-[#C85A32] hover:bg-[#B04B26]" data-testid="success-back-dashboard">
            Back to dashboard
          </Button>
        </>}
        {(status === "failed" || status === "error") && <>
          <XCircle className="mx-auto text-red-600" size={48}/>
          <div className="font-serif-display text-2xl font-bold mt-4">Payment didn't go through</div>
          <p className="text-stone-600 mt-2">Please try again or contact support.</p>
          <Button asChild variant="outline" className="mt-6"><Link to="/upgrade">Back to upgrade</Link></Button>
        </>}
        {status === "timeout" && <>
          <Loader2 className="mx-auto text-stone-400" size={40}/>
          <div className="font-serif-display text-2xl font-bold mt-4">Still processing</div>
          <p className="text-stone-600 mt-2">Your bank is taking longer than usual. We'll update your plan the moment we hear back.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6" variant="outline">Continue</Button>
        </>}
      </div>
    </div>
  );
}
